import { useState } from "react";
import { clearStorageKeepingAttribution } from "../../../../utils/attribution";
import {
  IconLayoutDashboard,
  IconHeartHandshake,
  IconRss,
  IconBulb,
  IconBriefcase,
  IconFileText,
  IconSearch,
  IconUserCircle,
  IconSettings,
  IconLogout,
  IconHelpCircle,
  IconVideo,
} from "@tabler/icons-react";
import styles from "./sidebar.module.css";
import { NavLink, useNavigate } from "react-router-dom";
import ErrorModal from "../../../../layout/ErrorModal/ErrorModal";
import confusedStan from "../../../../assets/illustrations/confused-stan-blue.png";

const ICON_SIZE = 18;

const NAV_ITEMS = [
  { icon: IconLayoutDashboard, label: "Dashboard", link: "/talent-dashboard" },
  { icon: IconHeartHandshake, label: "Matches", link: "/matches", gated: true },
  { icon: IconRss, label: "Job Feed", link: "/job-feed" },
  { icon: IconBulb, label: "AI Insights", link: "/ai-insights" },
  { icon: IconBriefcase, label: "Self Development", link: "/self-development" },
  { icon: IconFileText, label: "Test", link: "/test" },
  { icon: IconSearch, label: "Advanced Search", link: "/search" },
  { icon: IconVideo, label: "Media", link: "/media", gated: true },
];

const ACCOUNT_NAV = [
  { icon: IconUserCircle, label: "My Profile", link: "/talent-profile" },
  { icon: IconSettings, label: "Settings", link: "/talent-settings" },
];

export default function TalentSidebar({ collapsed, mobileOpen, onOverlayClick, data }) {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Gate only on a positive "unverified". The context profile starts as {}, which
  // is truthy, so the previous `data ? ... : false` guard still treated a profile
  // that had not loaded yet as unverified and gated the nav on every cold load.
  const isInactive = data?.profileVerified === false;

  const sidebarClasses = [styles.sidebar, collapsed ? styles.sidebarCollapsed : "", mobileOpen ? styles.sidebarMobileOpen : ""]
    .filter(Boolean)
    .join(" ");

  const logout = () => {
    clearStorageKeepingAttribution();
    navigate("/");
    window.location.reload();
  };

  const goToSupport = () => {
    navigate("/talent-support");
    setIsModalOpen(false);
  };

  return (
    <>
      <div className={`${styles.sidebarOverlay} ${mobileOpen ? styles.sidebarOverlayOpen : ""}`} onClick={onOverlayClick} />

      <nav className={sidebarClasses} aria-label="Main navigation">
        <div className={styles.sidebarNav}>
          {NAV_ITEMS.map(({ icon: Icon, label, link, gated }) =>
            gated && isInactive ? (
              <button key={label} className={styles.navItem} onClick={() => setIsModalOpen(true)}>
                <Icon size={ICON_SIZE} />
                <span className={styles.navText}>{label}</span>
              </button>
            ) : (
              <NavLink
                key={label}
                to={link}
                className={({ isActive }) => (isActive ? `${styles.navItemActive} ${styles.navItem}` : styles.navItem)}
              >
                <Icon size={ICON_SIZE} />
                <span className={styles.navText}>{label}</span>
              </NavLink>
            ),
          )}

          <div className={styles.navCollapsedDivider} />

          <div className={styles.navLabel}>Account</div>
          {ACCOUNT_NAV.map(({ icon: Icon, label, link }) => (
            <NavLink
              key={label}
              to={link}
              className={({ isActive }) => (isActive ? `${styles.navItemActive} ${styles.navItem}` : styles.navItem)}
            >
              <Icon size={ICON_SIZE} />
              <span className={styles.navText}>{label}</span>
            </NavLink>
          ))}

          <div className={styles.navCollapsedDivider} />

          <button onClick={logout} className={styles.navItem}>
            <IconLogout size={ICON_SIZE} />
            <span className={styles.navText}>Logout</span>
          </button>
          <NavLink to="/talent-support" className={({ isActive }) => (isActive ? `${styles.navItemActive} ${styles.navItem}` : styles.navItem)}>
            <IconHelpCircle size={ICON_SIZE} />
            <span className={styles.navText}>Support</span>
          </NavLink>
        </div>
      </nav>

      <ErrorModal isModalOpen={isModalOpen} setModalOpen={setIsModalOpen} hasCloseIcon>
        <div className={styles.modal}>
          <img className={styles.modalImage} src={confusedStan} alt="Confused Stan" />
          <p>Your profile hasn't been verified yet. Once your account is verified, you'll get access to this.</p>
          <button onClick={goToSupport} className={styles.modalButton}>
            Contact Support
          </button>
        </div>
      </ErrorModal>
    </>
  );
}
